---
title: "How many full weeks are between two dates, and what happens to extra days"
description: "Turn a date difference into full weeks plus remaining days, understand 2.43 weeks, and decide whether the end date belongs in the count."
locale: "en"
date: "2026-08-03"
tags: ["weeks between dates", "date difference", "calendar days"]
relatedTools: ["date-difference"]
translations:
  ru: "skolko-polnyh-nedel-mezhdu-datami"
faq:
  - q: "How do I calculate full weeks between two dates?"
    a: "Find the number of calendar days, then divide by 7. The integer part is the number of full weeks, and the remainder is the number of extra days."
  - q: "Is the end date included?"
    a: "By default, dateify measures the distance between the boundaries and does not add the end date. The include-end-date option adds one day to the calendar-day count and its derived week results."
  - q: "Does a full week have to run from Monday through Sunday?"
    a: "No. In this calculator it is any continuous block of 7 calendar days. Use the separate ISO week tool when you need a calendar week number."
---

A sprint starts on Monday, August 3, 2026, and release is set for Thursday,
August 20. The status report needs a duration, not "17 days" and not "the third
week of August." The useful answer is 2 full weeks and 3 days.

Open the [date difference calculator](/raznica-mezhdu-datami) and enter August 3
and August 20. In its default mode it shows 17 calendar days, `2.43` total
weeks, and a separate `2 wk 3 d` result. The last form is usually easier to put
into a plan because the remainder is visible without decoding a decimal.

The arithmetic is short:

```text
17 / 7 = 2 full weeks, remainder 3 days
```

`2.43 weeks` and `2 weeks 3 days` describe the same span. The decimal part
`0.43` is not 43 days, and it does not mean that a third full week has elapsed.
It is roughly three sevenths of a week.

## The end date changes the remainder

The distance from August 3 to August 20 is 17 days. That convention fits a
deadline measured from the start of Monday to the start of Thursday.

If the requirement says "August 3 through August 20, inclusive," turn on
Include end date. The result becomes 18 days, or 2 full weeks and 4 days. One
option changes calendar days, decimal weeks, and the remainder together. Adding
one only to the day label while keeping the old week result would describe two
different intervals.

Equal dates make a useful boundary test. The default distance is zero. With the
end date included, that same range counts as one calendar day. This tiny example
usually exposes whether a ticket and its implementation use the same rule.

If the dates are entered in reverse order, dateify swaps them and returns a
positive duration. The result calls out that swap instead of quietly hiding the
input mistake.

## Full weeks are not calendar weeks

A full week in this calculation is any continuous seven-day block. A full week
can begin on any weekday; Monday-to-Sunday calendar boundaries do not affect
the result.

For "which ISO week contains August 20," use the
[ISO week number tool](/nomer-nedeli). That is a different job: it returns the
ISO year, week number, and Monday-to-Sunday range. Counting calendar rows does
not replace measuring the span between two dates.

Working weeks need another calculation too. A 17-calendar-day period contains
weekends, and a Russian schedule may contain federal holidays or transferred
days off. The [business days calculator](/rabochie-dni-mezhdu-datami) can use
the approved federal five-day calendar for covered years. It does not guess
regional holidays or an individual's shift pattern.

## These are dates, not elapsed hours

The calculator accepts calendar dates without a time or time zone. It answers
in days and weeks; it does not claim that two selected midnights are always
exactly `N x 24` elapsed hours apart. An interval with clock time or a
daylight-saving transition needs time-aware duration semantics that this page
does not provide.

The broader workflow for months, years, and business days is covered in
[how to calculate date difference](/blog/how-to-calculate-date-difference).
For a weeks calculation, settle one question first: does the end date belong in
the period? Once that boundary is explicit, division by 7 is the easy part.
